import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import { TRY_ADD_CARD_AGAIN_STEP } from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useInstantBankingCard from '#client/hooks/useInstantBankingCard';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import useToastContext from '#client/hooks/useToastContext';
import { ACCEPT_ASTRA_TERMS_MUTATION } from '#client/mutations';

import Item from './Item';

import { Wrapper, BoxStyled, DialogContentStyled } from './styles';

const introduces = [
  {
    title: 'Fast',
    description: 'Speeds up settlement. Most payments process within 2 minutes.',
    icon: '/s2/icon/check-outline.svg'
  },
  {
    title: 'Secure',
    description: 'Keeps financial data safe with identity verification and bank-level security.',
    icon: '/s2/icon/web-hyperlink-outline.svg'
  },
  {
    title: 'Always on',
    description: 'Available 24/7/365, including weekends and bank holidays.',
    icon: '/s2/icon/web-hyperlink-outline.svg'
  }
];

export default function Content({ handleClose }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { openModal } = useModalContext();
  const { isNeedCreateDestinationCard, refreshAstraProfile } = useAstraProfileContext();

  const { getAndRedirectAstraUrl } = useOpenAstraModal();
  const { createBankingCardForInstantFunding, loading: createCardLoading } =
    useInstantBankingCard();

  const [acceptMutation, { loading: astraTermsLoading }] = useMutation(
    ACCEPT_ASTRA_TERMS_MUTATION,
    {
      noThrow: true
    }
  );

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'agree_astra_t&c',
    elementText: 'I agree',
    appPageName: 'astra_consent_modal'
  });

  /**
   * Function to handle authorize
   * - call acceptMutation to accept astra terms
   * - if error, show toast
   * - if success, call getAndRedirectAstraUrl to get astra url and redirect to that url
   */
  const handleAuthorize = async () => {
    buttonPressedTracker();
    const { error } = await acceptMutation({
      variables: {
        businessEntityUuid
      }
    });

    if (error) {
      createCommonErrorToast({ error });
      return;
    }

    // When change accept term successfully, we need refresh the astra profile to get lasted accept term status
    if (typeof refreshAstraProfile === 'function') {
      refreshAstraProfile();
    }

    if (isNeedCreateDestinationCard) {
      const { error: createCardError } = await createBankingCardForInstantFunding();
      if (createCardError) {
        openModal({
          component: InstantDepositModal,
          initStep: TRY_ADD_CARD_AGAIN_STEP
        });

        return;
      }
    }

    await getAndRedirectAstraUrl();

    setTimeout(handleClose, 2000);
  };

  useModalDisplayTracker({
    elementName: 'astra_consent_modal',
    elementText: 'astra_consent_modal',
    appPageName: 'active_banking_dashboard'
  });

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <Stack
                alignItems="center"
                gap={{
                  xs: '8px',
                  md: '16px'
                }}
                pb={{ xs: '32px', md: '40px' }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  We partner with Astra for faster payments
                </Heading>
                <Text
                  color="primary"
                  size="lg"
                >
                  Astra helps process transfers between bank accounts and cards — faster than ever
                  before.
                </Text>
              </Stack>
              <BoxStyled>
                <Heading
                  size="2xl"
                  weight="bold"
                >
                  Why Instant Deposit?
                </Heading>
                {introduces.map((introduce) => (
                  <Item
                    key={introduce.title}
                    {...introduce}
                  />
                ))}
              </BoxStyled>
              <Box mt="24px">
                <Text size="sm">
                  By selecting “I agree,” you agree to the Astra{' '}
                  <Link
                    color="info"
                    href="https://astrafi.com/terms/"
                    target="_blank"
                    underline="always"
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    color="info"
                    href="https://astrafi.com/privacy/"
                    target="_blank"
                    underline="always"
                  >
                    Privacy Policy
                  </Link>
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          fullWidth
          loading={astraTermsLoading || createCardLoading}
          onClick={handleAuthorize}
          size="large"
          variant="contained"
        >
          I agree
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

Content.propTypes = {
  handleClose: PropTypes.func.isRequired
};
